import { type ApplianceType } from './leak-rate.js'

export interface LeakCostInput {
    applianceType:ApplianceType;
    pricePerLb:number;
    lbsLostPerEvent:number;
    eventsPerYear:number;
    fullCharge:number;
    efficiencyLossPercent:number;
    pricePerKwh:number;
    annualKwh:number;
    routineCallCost:number;
    emergencyCallsPerYear:number;
    emergencyCallCost:number;
}

export interface LeakCostBreakdown {
    replacement:number;
    energy:number;
    routine:number;
    emergency:number;
}

export interface LeakCostResult {
    annualTotal:number;
    monthlyTotal:number;
    fiveYearTotal:number;
    breakdown:LeakCostBreakdown;
    breakdownPercent:Record<keyof LeakCostBreakdown, number>;
    leakRatePercent:number;
    leakRateThreshold:number|null;
    exceedsThreshold:boolean|null;
}

const THRESHOLDS:Record<ApplianceType, number|null> = {
    commercial: 20,
    industrial: 30,
    comfort: 10,
    other: null,
}

export function calcLeakCost (input:LeakCostInput):LeakCostResult {
    const {
        applianceType,
        pricePerLb,
        lbsLostPerEvent,
        eventsPerYear,
        fullCharge,
        efficiencyLossPercent,
        pricePerKwh,
        annualKwh,
        routineCallCost,
        emergencyCallsPerYear,
        emergencyCallCost,
    } = input

    const replacement = lbsLostPerEvent * eventsPerYear * pricePerLb
    const energy = annualKwh * pricePerKwh * (efficiencyLossPercent / 100)
    const routine = routineCallCost * eventsPerYear
    const emergency = emergencyCallsPerYear * emergencyCallCost

    const annualTotal = replacement + energy + routine + emergency
    const monthlyTotal = annualTotal / 12
    const fiveYearTotal = annualTotal * 5

    const breakdown:LeakCostBreakdown = {
        replacement,
        energy,
        routine,
        emergency,
    }

    const breakdownPercent = annualTotal > 0 ?
        {
            replacement: (replacement / annualTotal) * 100,
            energy: (energy / annualTotal) * 100,
            routine: (routine / annualTotal) * 100,
            emergency: (emergency / annualTotal) * 100,
        } :
        { replacement: 0, energy: 0, routine: 0, emergency: 0 }

    const lbsLostPerYear = lbsLostPerEvent * eventsPerYear
    const leakRatePercent = (lbsLostPerYear / fullCharge) * 100
    const leakRateThreshold = THRESHOLDS[applianceType]
    const exceedsThreshold =
        leakRateThreshold !== null ?
            leakRatePercent > leakRateThreshold :
            null

    return {
        annualTotal,
        monthlyTotal,
        fiveYearTotal,
        breakdown,
        breakdownPercent,
        leakRatePercent,
        leakRateThreshold,
        exceedsThreshold,
    }
}
